<template>
  <AppLayout>
    <div class="p-4 md:p-6">

      <!-- Header (desktop only) -->
      <div class="hidden md:flex items-center justify-between mb-4">
        <div>
          <h1 class="text-2xl font-bold text-slate-800 dark:text-white">Invoices</h1>
          <p class="text-slate-500 dark:text-slate-400 text-sm mt-0.5">
            <span v-if="dataLoading" class="inline-block h-3 w-28 bg-slate-200 dark:bg-slate-700 rounded animate-pulse align-middle"></span>
            <span v-else>{{ meta.total }} total invoice{{ meta.total !== 1 ? 's' : '' }}</span>
          </p>
        </div>
        <Link href="/invoices/new"
          class="flex items-center gap-2 bg-indigo-500 hover:bg-indigo-400 active:scale-95 text-white
                 text-sm font-semibold px-4 py-2.5 rounded-xl shadow-sm shadow-indigo-500/30 transition">
          <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15"/>
          </svg>
          New Invoice
        </Link>
      </div>

      <!-- ── MOBILE HEADER ── -->
      <div class="md:hidden -mx-4 -mt-4 mb-1">
        <!-- Gradient hero -->
        <div class="bg-gradient-to-br from-indigo-600 via-indigo-500 to-violet-500 px-4 pt-3 pb-6">
          <div class="flex items-center justify-between mb-3">
            <div>
              <h1 class="text-xl font-bold text-white">Invoices</h1>
              <p class="text-indigo-200 text-xs mt-0.5">
                <span v-if="dataLoading">Loading...</span>
                <span v-else>{{ meta.total }} invoice{{ meta.total !== 1 ? 's' : '' }}</span>
              </p>
            </div>
            <Link href="/invoices/new"
              class="flex items-center gap-1.5 bg-white/20 active:bg-white/30 text-white text-sm font-semibold px-4 py-2.5 rounded-xl transition">
              <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15"/>
              </svg>
              Add
            </Link>
          </div>
          <!-- Stats row -->
          <div v-if="!dataLoading" class="grid grid-cols-3 gap-2 mb-2">
            <div class="bg-white/15 backdrop-blur-sm rounded-2xl px-3 py-3 text-center">
              <p class="text-[9px] font-bold text-indigo-200 uppercase tracking-widest mb-1">Total</p>
              <p class="text-sm font-bold text-white">{{ meta.total }}</p>
            </div>
            <div class="bg-white/15 backdrop-blur-sm rounded-2xl px-3 py-3 text-center">
              <p class="text-[9px] font-bold text-emerald-200 uppercase tracking-widest mb-1">Sales</p>
              <p class="text-sm font-bold text-white whitespace-nowrap">Rs {{ Math.floor(mobileStatSales).toLocaleString('en-PK') }}</p>
            </div>
            <div class="bg-white/15 backdrop-blur-sm rounded-2xl px-3 py-3 text-center">
              <p class="text-[9px] font-bold text-blue-200 uppercase tracking-widest mb-1">Purchases</p>
              <p class="text-sm font-bold text-white whitespace-nowrap">Rs {{ Math.floor(mobileStatPurchases).toLocaleString('en-PK') }}</p>
            </div>
          </div>
          <div v-else class="grid grid-cols-3 gap-2 mb-2">
            <div v-for="i in 3" :key="i" class="bg-white/15 rounded-2xl h-14 animate-pulse"></div>
          </div>
        </div>

        <!-- Filter card pulls up over hero -->
        <div class="bg-gray-50 dark:bg-slate-950 rounded-t-3xl -mt-4 px-4 pt-2.5 pb-1.5">
          <div class="flex items-center gap-2">
            <!-- Type pills -->
            <button @click="f.invoice_action = ''; applyFilters()"
              class="shrink-0 px-3.5 py-2 rounded-full text-xs font-semibold transition active:scale-95"
              :class="!f.invoice_action ? 'bg-indigo-500 text-white shadow-sm' : 'bg-white dark:bg-slate-800 text-slate-500 dark:text-slate-400 border border-slate-200 dark:border-slate-700'">
              All
            </button>
            <button @click="f.invoice_action = 'sold'; applyFilters()"
              class="shrink-0 px-3.5 py-2 rounded-full text-xs font-semibold transition active:scale-95"
              :class="f.invoice_action === 'sold' ? 'bg-emerald-500 text-white shadow-sm' : 'bg-white dark:bg-slate-800 text-slate-500 dark:text-slate-400 border border-slate-200 dark:border-slate-700'">
              Sale
            </button>
            <button @click="f.invoice_action = 'purchased'; applyFilters()"
              class="shrink-0 px-3.5 py-2 rounded-full text-xs font-semibold transition active:scale-95"
              :class="f.invoice_action === 'purchased' ? 'bg-blue-500 text-white shadow-sm' : 'bg-white dark:bg-slate-800 text-slate-500 dark:text-slate-400 border border-slate-200 dark:border-slate-700'">
              Purchase
            </button>

            <div class="flex-1"></div>

            <!-- Filters button with badge -->
            <button @click="mobileFiltersSheetOpen = true"
              class="relative shrink-0 flex items-center gap-1.5 text-xs font-semibold px-3.5 py-2 rounded-full transition active:scale-95"
              :class="mobileFiltersActiveCount > 0 ? 'bg-indigo-500 text-white shadow-sm' : 'bg-white dark:bg-slate-800 text-slate-500 dark:text-slate-400 border border-slate-200 dark:border-slate-700'">
              <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M10.5 6h9.75M10.5 6a1.5 1.5 0 11-3 0m3 0a1.5 1.5 0 10-3 0M3.75 6H7.5m3 12h9.75m-9.75 0a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m-3.75 0H7.5m9-6h3.75m-3.75 0a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m-9.75 0h9.75"/>
              </svg>
              Filters
              <span v-if="mobileFiltersActiveCount > 0"
                class="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-white text-indigo-600 text-[10px] font-bold flex items-center justify-center shadow">
                {{ mobileFiltersActiveCount }}
              </span>
            </button>
          </div>
        </div>
      </div>

      <!-- Filters — Desktop -->
      <div class="hidden md:flex flex-wrap gap-2 mb-4">

        <!-- Customer searchable dropdown -->
        <div class="relative" ref="filterCustomerRef">
          <button type="button" @click="toggleCustomerDropdown"
            class="flex items-center gap-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700
                   text-slate-700 dark:text-slate-300 rounded-xl pl-3 pr-3 py-2.5 text-sm
                   focus:outline-none focus:ring-2 focus:ring-indigo-500 transition cursor-pointer min-w-[140px]">
            <span class="flex-1 text-left truncate">{{ filterCustomerLabel }}</span>
            <svg class="w-3.5 h-3.5 text-slate-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
              <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5"/>
            </svg>
          </button>
          <div v-if="customerDropdownOpen"
            class="absolute z-50 mt-1 w-56 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-lg overflow-hidden">
            <div class="p-2 border-b border-slate-100 dark:border-slate-700">
              <input v-model="customerSearch" type="text" placeholder="Search by name or shop..."
                class="w-full bg-slate-50 dark:bg-slate-900 text-slate-800 dark:text-white placeholder-slate-400
                       rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                @click.stop
                @keydown.down.prevent="moveCustomer(1)"
                @keydown.up.prevent="moveCustomer(-1)"
                @keydown.enter.prevent="confirmCustomer"
                @keydown.escape="customerDropdownOpen = false" />
            </div>
            <div class="max-h-48 overflow-y-auto">
              <button type="button" @click="selectCustomer('')"
                class="w-full text-left px-3 py-2.5 text-sm transition"
                :class="!f.customer_id ? 'bg-indigo-50 dark:bg-indigo-950 text-indigo-600 dark:text-indigo-400 font-semibold' : 'text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800'">
                All Customers
              </button>
              <button v-for="(c, idx) in filteredCustomers" :key="c.id" type="button"
                :ref="el => { if (el) customerItemRefs[idx] = el }"
                @click="selectCustomer(String(c.id))"
                class="w-full text-left px-3 py-2.5 text-sm transition"
                :class="idx === activeCustomerIdx
                  ? 'bg-indigo-50 dark:bg-indigo-950 text-indigo-600 dark:text-indigo-400 font-semibold'
                  : String(c.id) === f.customer_id
                    ? 'text-indigo-600 dark:text-indigo-400 font-semibold'
                    : 'text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800'">
                <p>{{ c.name }}</p>
                <p class="text-xs text-slate-400">{{ c.shop_name }}</p>
              </button>
              <p v-if="filteredCustomers.length === 0" class="px-3 py-4 text-sm text-slate-400 text-center">No customers found</p>
            </div>
          </div>
        </div>

        <!-- Invoice type filter -->
        <div class="relative">
          <select v-model="f.invoice_action" @change="applyFilters"
            class="appearance-none bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700
                   text-slate-700 dark:text-slate-300 rounded-xl pl-3 pr-8 py-2.5 text-sm
                   focus:outline-none focus:ring-2 focus:ring-indigo-500 transition cursor-pointer">
            <option value="">All Types</option>
            <option value="sold">Sale</option>
            <option value="purchased">Purchase</option>
          </select>
          <svg class="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400"
            fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
            <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5"/>
          </svg>
        </div>

        <!-- Date preset -->
        <div class="relative">
          <select v-model="f.date_preset" @change="onDatePresetChange"
            class="appearance-none bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700
                   text-slate-700 dark:text-slate-300 rounded-xl pl-3 pr-8 py-2.5 text-sm
                   focus:outline-none focus:ring-2 focus:ring-indigo-500 transition cursor-pointer">
            <option value="">All Time</option>
            <option value="today">Today</option>
            <option value="yesterday">Yesterday</option>
            <option value="this_week">This Week</option>
            <option value="last_week">Last Week</option>
            <option value="this_month">This Month</option>
            <option value="last_month">Last Month</option>
            <option value="this_year">This Year</option>
            <option value="last_year">Last Year</option>
            <option value="custom">Custom...</option>
          </select>
          <svg class="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400"
            fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
            <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5"/>
          </svg>
        </div>

        <!-- Custom date range -->
        <template v-if="f.date_preset === 'custom'">
          <input v-model="f.date_from" type="date" @change="applyFilters"
            class="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700
                   text-slate-700 dark:text-slate-300 rounded-xl px-3 py-2.5 text-sm
                   focus:outline-none focus:ring-2 focus:ring-indigo-500 transition" />
          <span class="self-center text-slate-400 text-sm">to</span>
          <input v-model="f.date_to" type="date" @change="applyFilters"
            class="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700
                   text-slate-700 dark:text-slate-300 rounded-xl px-3 py-2.5 text-sm
                   focus:outline-none focus:ring-2 focus:ring-indigo-500 transition" />
        </template>

        <!-- Clear -->
        <button v-if="hasFilters" @click="clearFilters"
          class="flex items-center gap-1.5 text-sm text-slate-500 dark:text-slate-400 hover:text-slate-700
                 dark:hover:text-slate-200 bg-slate-100 dark:bg-slate-800 px-3 py-2.5 rounded-xl transition">
          <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
          </svg>
          Clear
        </button>
      </div>

      <!-- Flash -->
      <div v-if="$page.props.flash?.notice"
        class="mb-4 flex items-center gap-2 bg-green-50 dark:bg-green-950 text-green-700 dark:text-green-400 text-sm px-4 py-3 rounded-xl">
        <svg class="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
        </svg>
        {{ $page.props.flash.notice }}
      </div>

      <!-- Skeleton -->
      <template v-if="dataLoading">
        <!-- Desktop skeleton -->
        <div class="hidden md:block bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 overflow-hidden animate-pulse">
          <table class="w-full text-sm">
            <thead>
              <tr class="border-b border-slate-100 dark:border-slate-800">
                <th class="text-left px-5 py-3.5 text-xs font-semibold text-slate-400 uppercase tracking-wide">#</th>
                <th class="text-left px-5 py-3.5 text-xs font-semibold text-slate-400 uppercase tracking-wide">Date</th>
                <th class="text-left px-5 py-3.5 text-xs font-semibold text-slate-400 uppercase tracking-wide">Customer</th>
                <th class="text-left px-5 py-3.5 text-xs font-semibold text-slate-400 uppercase tracking-wide">Type</th>
                <th class="text-center px-5 py-3.5 text-xs font-semibold text-slate-400 uppercase tracking-wide">Items</th>
                <th class="text-right px-5 py-3.5 text-xs font-semibold text-slate-400 uppercase tracking-wide">Total</th>
                <th class="text-left px-5 py-3.5 text-xs font-semibold text-slate-400 uppercase tracking-wide">Notes</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-slate-50 dark:divide-slate-800">
              <tr v-for="i in 8" :key="i">
                <td class="px-5 py-4"><div class="h-3 w-10 bg-slate-200 dark:bg-slate-700 rounded"></div></td>
                <td class="px-5 py-4"><div class="h-3 w-20 bg-slate-200 dark:bg-slate-700 rounded"></div></td>
                <td class="px-5 py-4">
                  <div class="h-3 w-28 bg-slate-200 dark:bg-slate-700 rounded mb-1.5"></div>
                  <div class="h-2.5 w-20 bg-slate-100 dark:bg-slate-800 rounded"></div>
                </td>
                <td class="px-5 py-4"><div class="h-5 w-16 bg-slate-200 dark:bg-slate-700 rounded-lg"></div></td>
                <td class="px-5 py-4 text-center"><div class="h-3 w-4 bg-slate-200 dark:bg-slate-700 rounded mx-auto"></div></td>
                <td class="px-5 py-4 text-right"><div class="h-3 w-16 bg-slate-200 dark:bg-slate-700 rounded ml-auto"></div></td>
                <td class="px-5 py-4"><div class="h-3 w-20 bg-slate-100 dark:bg-slate-800 rounded"></div></td>
              </tr>
            </tbody>
          </table>
        </div>
        <!-- Mobile skeleton -->
        <div class="md:hidden space-y-3 animate-pulse">
          <div v-for="i in 5" :key="i" class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 p-4">
            <div class="flex items-start justify-between mb-3">
              <div>
                <div class="h-3.5 w-28 bg-slate-200 dark:bg-slate-700 rounded mb-1.5"></div>
                <div class="h-2.5 w-36 bg-slate-100 dark:bg-slate-800 rounded"></div>
              </div>
              <div class="h-6 w-16 bg-slate-200 dark:bg-slate-700 rounded-lg"></div>
            </div>
            <div class="flex items-center justify-between">
              <div class="h-2.5 w-12 bg-slate-100 dark:bg-slate-800 rounded"></div>
              <div class="h-3.5 w-20 bg-slate-200 dark:bg-slate-700 rounded"></div>
            </div>
          </div>
        </div>
      </template>

      <!-- Empty -->
      <div v-else-if="invoices.length === 0" class="flex flex-col items-center justify-center py-24 text-center">
        <div class="w-16 h-16 rounded-2xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center mb-4">
          <svg class="w-8 h-8 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
            <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"/>
          </svg>
        </div>
        <p class="text-slate-600 dark:text-slate-400 font-medium">{{ hasFilters ? 'No invoices match your filters' : 'No invoices yet' }}</p>
        <p class="text-slate-400 dark:text-slate-500 text-sm mt-1">{{ hasFilters ? 'Try adjusting your filters' : 'Create your first invoice to get started' }}</p>
        <button v-if="hasFilters" @click="clearFilters" class="mt-4 text-indigo-500 text-sm font-semibold hover:text-indigo-400 transition">Clear filters</button>
        <Link v-else href="/invoices/new" class="mt-4 text-indigo-500 text-sm font-semibold hover:text-indigo-400 transition">+ New Invoice</Link>
      </div>

      <template v-else-if="!dataLoading">
        <!-- Desktop table -->
        <div class="hidden md:block bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 overflow-visible">
          <table class="w-full text-sm">
            <thead>
              <tr class="border-b border-slate-100 dark:border-slate-800">
                <th class="text-left px-5 py-3.5 text-xs font-semibold text-slate-400 uppercase tracking-wide">#</th>
                <th class="text-left px-5 py-3.5 text-xs font-semibold text-slate-400 uppercase tracking-wide">Date</th>
                <th class="text-left px-5 py-3.5 text-xs font-semibold text-slate-400 uppercase tracking-wide">Customer</th>
                <th class="text-left px-5 py-3.5 text-xs font-semibold text-slate-400 uppercase tracking-wide">Type</th>
                <th class="text-center px-5 py-3.5 text-xs font-semibold text-slate-400 uppercase tracking-wide">Items</th>
                <th class="text-right px-5 py-3.5 text-xs font-semibold text-slate-400 uppercase tracking-wide">Total</th>
                <th class="px-5 py-3.5"></th>
              </tr>
            </thead>
            <tbody class="divide-y divide-slate-50 dark:divide-slate-800">
              <template v-for="(inv, i) in invoices" :key="inv.id">
                <tr class="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition cursor-pointer"
                  @click="toggleExpand(inv.id)">
                  <td class="px-5 py-4 text-slate-400 text-xs">#{{ inv.invoice_number }}</td>
                  <td class="px-5 py-4 text-slate-600 dark:text-slate-400 whitespace-nowrap">{{ formatDate(inv.target_date) }}</td>
                  <td class="px-5 py-4">
                    <div class="flex items-center gap-2">
                      <svg class="w-3.5 h-3.5 text-slate-400 shrink-0 transition-transform duration-200"
                        :class="expandedId === inv.id ? 'rotate-90 text-indigo-500' : ''"
                        fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5"/>
                      </svg>
                      <div>
                        <p class="font-semibold text-slate-800 dark:text-white">{{ inv.customer.name }}</p>
                        <p class="text-xs text-slate-400">{{ inv.customer.shop_name }}</p>
                      </div>
                    </div>
                  </td>
                  <td class="px-5 py-4">
                    <span :class="inv.action === 'sold'
                      ? 'bg-emerald-50 dark:bg-emerald-950 text-emerald-700 dark:text-emerald-400'
                      : 'bg-blue-50 dark:bg-blue-950 text-blue-700 dark:text-blue-400'"
                      class="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-semibold">
                      {{ inv.action === 'sold' ? 'Sale' : 'Purchase' }}
                    </span>
                  </td>
                  <td class="px-5 py-4 text-center text-slate-600 dark:text-slate-400">{{ inv.items_count }}</td>
                  <td class="px-5 py-4 text-right font-semibold text-slate-800 dark:text-white">Rs {{ formatNum(inv.total) }}</td>
                  <td class="px-5 py-4 text-center" @click.stop>
                    <div class="relative inline-block">
                      <button @click.stop="toggleActionDropdown(inv.id)"
                        class="inline-flex items-center justify-center w-8 h-8 text-slate-500 dark:text-slate-400
                               hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition">
                        <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"/>
                        </svg>
                      </button>
                      <!-- Dropdown menu -->
                      <div v-if="actionDropdownId === inv.id" @click.stop
                        class="absolute right-0 bottom-full mb-1 w-40 bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700 z-50 overflow-hidden">
                        <Link v-if="inv.can_edit" :href="`/invoices/${inv.id}/edit`" @click="actionDropdownId = null"
                          class="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-slate-700 dark:text-slate-300
                                 hover:bg-slate-50 dark:hover:bg-slate-700 transition">
                          <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931z"/>
                          </svg>
                          Edit
                        </Link>
                        <div v-else class="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-slate-300 dark:text-slate-600 cursor-not-allowed">
                          <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z"/>
                          </svg>
                          Edit Locked
                        </div>
                        <button @click="openDeleteConfirm(inv); actionDropdownId = null"
                          class="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-red-600 dark:text-red-400
                                 hover:bg-red-50 dark:hover:bg-red-950 transition border-t border-slate-100 dark:border-slate-700">
                          <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"/>
                          </svg>
                          Delete
                        </button>
                      </div>
                    </div>
                  </td>
                </tr>
                <!-- Expanded invoice detail -->
                <tr v-if="expandedId === inv.id">
                  <td colspan="7" class="px-6 pb-5 pt-0 bg-white dark:bg-slate-900">
                    <div class="border border-slate-200 dark:border-slate-700 rounded-2xl overflow-hidden">

                      <!-- Business + Invoice label -->
                      <div class="flex items-start justify-between gap-4 px-6 py-5
                                  border-b border-slate-100 dark:border-slate-700">
                        <div>
                          <p class="font-bold text-slate-800 dark:text-white text-base">
                            {{ $page.props.auth.user.business_name || 'Business' }}
                          </p>
                          <div class="mt-1 space-y-0.5 text-xs text-slate-400">
                            <p v-if="$page.props.auth.user.business_address">{{ $page.props.auth.user.business_address }}</p>
                            <p v-if="$page.props.auth.user.business_phone_number">{{ $page.props.auth.user.business_phone_number }}</p>
                            <p>{{ $page.props.auth.user.email }}</p>
                          </div>
                        </div>
                        <div class="text-right shrink-0">
                          <p class="text-xl font-extrabold tracking-tight"
                            :class="inv.action === 'sold' ? 'text-emerald-600 dark:text-emerald-400' : 'text-blue-600 dark:text-blue-400'">
                            INVOICE
                          </p>
                          <span :class="inv.action === 'sold'
                            ? 'bg-emerald-50 dark:bg-emerald-950 text-emerald-700 dark:text-emerald-400'
                            : 'bg-blue-50 dark:bg-blue-950 text-blue-700 dark:text-blue-400'"
                            class="inline-block mt-1 px-2 py-0.5 rounded-lg text-[10px] font-bold uppercase tracking-wide">
                            {{ inv.action === 'sold' ? 'Sale' : 'Purchase' }}
                          </span>
                        </div>
                      </div>

                      <!-- Bill To + Invoice meta + actions -->
                      <div class="flex items-start justify-between gap-4 px-6 py-4
                                  border-b border-slate-100 dark:border-slate-700">
                        <!-- Bill To -->
                        <div>
                          <p class="text-[10px] font-semibold text-slate-400 uppercase tracking-wide mb-1.5">Bill To</p>
                          <p class="font-bold text-slate-800 dark:text-white text-sm">{{ inv.customer.name }}</p>
                          <p class="text-xs text-slate-500 dark:text-slate-400">{{ inv.customer.shop_name }}</p>
                        </div>
                        <!-- Invoice details -->
                        <div class="text-right">
                          <p class="text-[10px] font-semibold text-slate-400 uppercase tracking-wide mb-1.5">Details</p>
                          <p class="text-xs text-slate-400">Invoice No. <span class="font-bold text-slate-700 dark:text-slate-200">#{{ inv.invoice_number }}</span></p>
                          <p class="text-xs text-slate-400 mt-0.5">Date <span class="font-semibold text-slate-700 dark:text-slate-200">{{ formatDate(inv.target_date) }}</span></p>
                          <!-- Action buttons -->
                          <div class="flex items-center justify-end gap-2 mt-3">
                            <!-- Public toggle -->
                            <button @click.stop="togglePublic(inv)" type="button"
                              class="flex items-center gap-2 text-xs font-semibold px-2.5 py-1.5 rounded-lg transition"
                              :class="inv.public
                                ? 'text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950'
                                : 'text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700'">
                              <span class="relative inline-flex w-7 h-4 shrink-0">
                                <span class="absolute inset-0 rounded-full transition"
                                  :class="inv.public ? 'bg-emerald-500' : 'bg-slate-300 dark:bg-slate-600'"></span>
                                <span class="absolute top-0.5 left-0.5 w-3 h-3 rounded-full bg-white shadow transition-transform"
                                  :class="inv.public ? 'translate-x-3' : 'translate-x-0'"></span>
                              </span>
                              {{ inv.public ? 'Public' : 'Private' }}
                            </button>
                            <button @click.stop="copyInvoiceLink(inv.id)" :disabled="!inv.public"
                              class="flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1.5 rounded-lg transition"
                              :class="inv.public
                                ? 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700'
                                : 'text-slate-300 dark:text-slate-600 cursor-not-allowed'">
                              <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                <path stroke-linecap="round" stroke-linejoin="round" d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m13.35-.622l1.757-1.757a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244"/>
                              </svg>
                              {{ copiedId === inv.id ? 'Copied!' : 'Share' }}
                            </button>
                          </div>
                        </div>
                      </div>

                      <!-- Notes section -->
                      <div v-if="inv.notes" class="px-6 py-4 border-b border-slate-100 dark:border-slate-700">
                        <p class="text-[10px] font-semibold text-slate-400 uppercase tracking-wide mb-1.5">Notes</p>
                        <p class="text-sm text-slate-700 dark:text-slate-300">{{ inv.notes }}</p>
                      </div>

                      <!-- Locked warning -->
                      <div v-if="!inv.can_edit" class="px-6 py-4 border-b border-slate-100 dark:border-slate-700">
                        <div class="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4">
                          <div class="flex items-start gap-3">
                            <svg class="w-5 h-5 text-amber-600 dark:text-amber-500 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                              <path stroke-linecap="round" stroke-linejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z"/>
                            </svg>
                            <div class="flex-1">
                              <p class="text-sm font-semibold text-amber-900 dark:text-amber-300">Cannot Edit Invoice</p>
                              <p class="text-xs text-amber-700 dark:text-amber-400 mt-1">
                                <span v-if="inv.customer_deleted">Customer has been deleted</span>
                                <span v-else-if="inv.has_deleted_products">The following products have been deleted: {{ inv.deleted_products_names.join(', ') }}</span>
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>

                      <!-- Items table -->
                      <table class="w-full text-sm">
                        <thead>
                          <tr class="border-b-2 border-slate-100 dark:border-slate-700">
                            <th class="text-left px-6 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wide w-6">#</th>
                            <th class="text-left px-3 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wide">Product</th>
                            <th class="text-center px-3 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wide">Qty</th>
                            <th class="text-right px-3 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wide">Rate</th>
                            <th class="text-right px-6 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wide">Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="(item, i) in inv.items" :key="item.id"
                            class="border-b border-slate-50 dark:border-slate-800"
                            :class="i % 2 !== 0 ? 'bg-slate-50/50 dark:bg-slate-800/20' : ''">
                            <td class="px-6 py-3 text-slate-400 text-xs">{{ i + 1 }}</td>
                            <td class="px-3 py-3 text-slate-700 dark:text-slate-300 font-medium">{{ item.product_name }}</td>
                            <td class="px-3 py-3 text-center text-slate-600 dark:text-slate-400">{{ item.quantity }}</td>
                            <td class="px-3 py-3 text-right text-slate-600 dark:text-slate-400">Rs {{ formatNum(item.rate) }}</td>
                            <td class="px-6 py-3 text-right font-semibold text-slate-800 dark:text-white">Rs {{ formatNum(item.total) }}</td>
                          </tr>
                        </tbody>
                      </table>

                      <!-- Total footer -->
                      <div class="flex justify-end px-6 py-4 bg-slate-50 dark:bg-slate-800/30">
                        <div class="min-w-[180px] space-y-2">
                          <div class="flex justify-between text-xs text-slate-500 dark:text-slate-400">
                            <span>Subtotal</span>
                            <span>Rs {{ formatNum(inv.total) }}</span>
                          </div>
                          <div class="flex justify-between font-bold text-sm border-t border-slate-200 dark:border-slate-700 pt-2"
                            :class="inv.action === 'sold' ? 'text-emerald-600 dark:text-emerald-400' : 'text-blue-600 dark:text-blue-400'">
                            <span>Total</span>
                            <span>Rs {{ formatNum(inv.total) }}</span>
                          </div>
                        </div>
                      </div>

                    </div>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>

        <!-- Mobile cards -->
        <div class="md:hidden space-y-2.5">
          <div v-for="inv in invoices" :key="inv.id"
            class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 overflow-hidden">

            <!-- Card header — tappable to expand -->
            <button @click="toggleExpand(inv.id)" class="w-full text-left p-4 active:bg-slate-50 dark:active:bg-slate-800/50 transition">
              <div class="flex items-start justify-between gap-3">
                <div class="min-w-0 flex-1">
                  <!-- Type badge + invoice number + date -->
                  <div class="flex items-center gap-2 mb-1.5">
                    <span class="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-md shrink-0"
                      :class="inv.action === 'sold'
                        ? 'bg-emerald-100 dark:bg-emerald-950 text-emerald-700 dark:text-emerald-400'
                        : 'bg-blue-100 dark:bg-blue-950 text-blue-700 dark:text-blue-400'">
                      {{ inv.action === 'sold' ? 'Sale' : 'Purchase' }}
                    </span>
                    <span class="text-[10px] text-slate-400 font-medium">#{{ inv.invoice_number }}</span>
                    <span class="text-[10px] text-slate-400">· {{ formatDate(inv.target_date) }}</span>
                  </div>
                  <!-- Customer -->
                  <p class="font-semibold text-slate-800 dark:text-white text-sm leading-tight truncate">{{ inv.customer.name }}</p>
                  <p class="text-xs text-slate-400 mt-0.5 truncate">{{ inv.customer.shop_name }}</p>
                </div>
                <!-- Amount + chevron -->
                <div class="shrink-0 text-right">
                  <p class="font-bold text-slate-800 dark:text-white text-base leading-tight">Rs {{ formatNum(inv.total) }}</p>
                  <p class="text-[10px] text-slate-400 mt-0.5">{{ inv.items_count }} item{{ inv.items_count !== 1 ? 's' : '' }}</p>
                  <svg class="w-4 h-4 text-slate-400 mt-1.5 ml-auto transition-transform duration-200"
                    :class="expandedId === inv.id ? 'rotate-180' : ''"
                    fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7"/>
                  </svg>
                </div>
              </div>
            </button>

            <!-- Expanded detail -->
            <div v-if="expandedId === inv.id"
              class="border-t border-slate-100 dark:border-slate-800">

              <!-- Items list -->
              <div class="px-4 pb-3 pt-2 space-y-2">
                <p class="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">Items</p>
                <div v-for="item in inv.items" :key="item.id"
                  class="flex items-center justify-between py-2 border-b border-slate-50 dark:border-slate-800 last:border-0">
                  <div class="min-w-0 flex-1">
                    <p class="text-xs font-semibold text-slate-700 dark:text-slate-300 truncate">{{ item.product_name }}</p>
                    <p class="text-[10px] text-slate-400 mt-0.5">{{ item.quantity }} × Rs {{ formatNum(item.rate) }}</p>
                  </div>
                  <p class="text-xs font-bold text-slate-800 dark:text-white shrink-0 ml-3">Rs {{ formatNum(item.total) }}</p>
                </div>
              </div>

              <!-- Total footer -->
              <div class="flex items-center justify-between px-4 py-3 border-b border-slate-100 dark:border-slate-800"
                :class="inv.action === 'sold' ? 'bg-emerald-50 dark:bg-emerald-950/40' : 'bg-blue-50 dark:bg-blue-950/40'">
                <span class="text-xs font-semibold"
                  :class="inv.action === 'sold' ? 'text-emerald-700 dark:text-emerald-400' : 'text-blue-700 dark:text-blue-400'">
                  Total
                </span>
                <span class="text-sm font-extrabold"
                  :class="inv.action === 'sold' ? 'text-emerald-700 dark:text-emerald-400' : 'text-blue-700 dark:text-blue-400'">
                  Rs {{ formatNum(inv.total) }}
                </span>
              </div>

              <!-- Action buttons row -->
              <div class="px-4 py-3 bg-slate-50 dark:bg-slate-800/50 space-y-2.5 rounded-b-2xl">
                <!-- Edit & Delete buttons in grid -->
                <div class="grid grid-cols-2 gap-2">
                  <Link v-if="inv.can_edit" :href="`/invoices/${inv.id}/edit`"
                    class="flex items-center justify-center gap-2 text-sm font-semibold text-indigo-600 dark:text-indigo-400
                           bg-white dark:bg-slate-900 border border-indigo-200 dark:border-indigo-800 rounded-xl py-2.5 transition active:scale-95">
                    <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125"/>
                    </svg>
                    Edit
                  </Link>
                  <button v-else @click.stop="handleLockedEditClick(inv)"
                    class="flex items-center justify-center gap-2 text-sm font-semibold text-slate-400 dark:text-slate-600
                           bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl py-2.5">
                    <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z"/>
                    </svg>
                    Locked
                  </button>
                  <button @click.stop="openDeleteConfirm(inv)"
                    class="flex items-center justify-center gap-2 text-sm font-semibold text-red-600 dark:text-red-400
                           bg-white dark:bg-slate-900 border border-red-200 dark:border-red-800 rounded-xl py-2.5 transition active:scale-95">
                    <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"/>
                    </svg>
                    Delete
                  </button>
                </div>

                <!-- Public/Private & Share buttons -->
                <div class="flex items-center gap-2">
                  <button @click="togglePublic(inv)" type="button"
                    class="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-xl transition active:scale-95"
                    :class="inv.public
                      ? 'bg-emerald-100 dark:bg-emerald-950 text-emerald-700 dark:text-emerald-400'
                      : 'bg-white dark:bg-slate-700 text-slate-500 dark:text-slate-400 border border-slate-200 dark:border-slate-600'">
                    <span class="relative inline-flex w-6 h-3.5 shrink-0">
                      <span class="absolute inset-0 rounded-full transition"
                        :class="inv.public ? 'bg-emerald-500' : 'bg-slate-300 dark:bg-slate-500'"></span>
                      <span class="absolute top-0.5 left-0.5 w-2.5 h-2.5 rounded-full bg-white shadow transition-transform"
                        :class="inv.public ? 'translate-x-2.5' : 'translate-x-0'"></span>
                    </span>
                    {{ inv.public ? 'Public' : 'Private' }}
                  </button>
                  <button @click="copyInvoiceLink(inv.id)" :disabled="!inv.public"
                    class="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-xl transition active:scale-95"
                    :class="inv.public
                      ? 'bg-white dark:bg-slate-700 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-600'
                      : 'text-slate-300 dark:text-slate-600 cursor-not-allowed'">
                    <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m13.35-.622l1.757-1.757a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244"/>
                    </svg>
                    {{ copiedId === inv.id ? 'Copied!' : 'Share' }}
                  </button>
                </div>
              </div>
            </div>

          </div>
        </div>

        <!-- Pagination — Desktop -->
        <div class="hidden md:flex flex-col sm:flex-row items-center justify-between gap-3 mt-5">
          <div class="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
            <span>Show</span>
            <div class="relative">
              <select v-model="f.per_page" @change="f.page = 1; fetchData()"
                class="appearance-none bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700
                       text-slate-700 dark:text-slate-300 rounded-xl pl-3 pr-7 py-1.5 text-sm
                       focus:outline-none focus:ring-2 focus:ring-indigo-500 transition cursor-pointer">
                <option v-for="n in perPageOptions" :key="n" :value="n">{{ n }}</option>
              </select>
              <svg class="pointer-events-none absolute right-1.5 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400"
                fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5"/>
              </svg>
            </div>
            <span>of {{ meta.total }} records</span>
          </div>
          <div class="flex items-center gap-1">
            <button @click="goToPage(meta.current_page - 1)" :disabled="meta.current_page <= 1"
              class="px-3 py-1.5 rounded-xl text-sm border border-slate-200 dark:border-slate-700
                     text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800
                     disabled:opacity-40 disabled:cursor-not-allowed transition">‹</button>
            <template v-for="pg in pageNumbers" :key="pg">
              <span v-if="pg === '...'" class="px-2 text-slate-400">…</span>
              <button v-else @click="goToPage(pg)"
                :class="pg === meta.current_page
                  ? 'bg-indigo-500 text-white border-indigo-500'
                  : 'border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800'"
                class="w-9 h-9 rounded-xl text-sm border transition">{{ pg }}</button>
            </template>
            <button @click="goToPage(meta.current_page + 1)" :disabled="meta.current_page >= meta.pages"
              class="px-3 py-1.5 rounded-xl text-sm border border-slate-200 dark:border-slate-700
                     text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800
                     disabled:opacity-40 disabled:cursor-not-allowed transition">›</button>
          </div>
        </div>

        <!-- Load more — Mobile -->
        <div class="md:hidden mt-4 flex flex-col items-center gap-3">
          <button v-if="meta.current_page < meta.pages" @click="loadMore" :disabled="loadingMore"
            class="flex items-center gap-2 px-6 py-2.5 rounded-2xl text-sm font-semibold border border-slate-200 dark:border-slate-700
                   bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-300
                   disabled:opacity-50 active:scale-95 transition w-full justify-center">
            <svg v-if="loadingMore" class="w-4 h-4 animate-spin text-indigo-500" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
            </svg>
            <span>{{ loadingMore ? 'Loading…' : 'Load more' }}</span>
          </button>
          <p v-else-if="meta.total > 0" class="text-xs text-slate-400">All caught up ✓</p>
        </div>
      </template>

    </div>

    <!-- ── Mobile filters bottom sheet ── -->
    <Teleport to="body">
      <Transition
        enter-active-class="transition-opacity duration-300"
        enter-from-class="opacity-0" enter-to-class="opacity-100"
        leave-active-class="transition-opacity duration-200"
        leave-from-class="opacity-100" leave-to-class="opacity-0">
        <div v-if="mobileFiltersSheetOpen"
          class="md:hidden fixed inset-0 z-40 bg-black/40 backdrop-blur-sm"
          @click="mobileFiltersSheetOpen = false" />
      </Transition>
      <Transition
        enter-active-class="transition-transform duration-300 ease-[cubic-bezier(0.32,0.72,0,1)]"
        enter-from-class="translate-y-full" enter-to-class="translate-y-0"
        leave-active-class="transition-transform duration-200 ease-in"
        leave-from-class="translate-y-0" leave-to-class="translate-y-full">
        <div v-if="mobileFiltersSheetOpen"
          class="md:hidden fixed inset-x-0 bottom-0 z-50 bg-white dark:bg-slate-900 rounded-t-3xl shadow-2xl flex flex-col"
          style="max-height: 82vh"
          @click.stop>
          <!-- Handle -->
          <div class="flex justify-center pt-3 pb-1 shrink-0">
            <div class="w-10 h-1 rounded-full bg-slate-300 dark:bg-slate-600"></div>
          </div>
          <!-- Header -->
          <div class="flex items-center justify-between px-4 py-3 border-b border-slate-100 dark:border-slate-800 shrink-0">
            <p class="font-semibold text-slate-800 dark:text-white text-base">Filters</p>
            <div class="flex items-center gap-2">
              <button v-if="hasFilters" @click="clearFilters(); mobileFiltersSheetOpen = false"
                class="text-xs font-semibold text-red-500 px-3 py-1.5 rounded-full bg-red-50 dark:bg-red-950/50 transition">
                Clear all
              </button>
              <button @click="mobileFiltersSheetOpen = false"
                class="w-8 h-8 flex items-center justify-center rounded-full bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400">
                <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </button>
            </div>
          </div>
          <!-- Content -->
          <div class="overflow-y-auto flex-1 px-4 py-4 space-y-5">
            <!-- Customer -->
            <div>
              <p class="text-xs font-bold text-slate-400 uppercase tracking-wide mb-2">Customer</p>
              <button type="button"
                @click="mobileFiltersSheetOpen = false; $nextTick(() => { mobileCustomerSheetOpen = true; customerSearch = '' })"
                class="w-full flex items-center justify-between bg-slate-50 dark:bg-slate-800 rounded-2xl px-4 py-3 text-sm transition active:bg-slate-100 dark:active:bg-slate-700">
                <span :class="f.customer_id ? 'text-indigo-600 dark:text-indigo-400 font-semibold' : 'text-slate-500 dark:text-slate-400'">
                  {{ filterCustomerLabel }}
                </span>
                <svg class="w-4 h-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5"/>
                </svg>
              </button>
            </div>
            <!-- Date range -->
            <div>
              <p class="text-xs font-bold text-slate-400 uppercase tracking-wide mb-2">Date Range</p>
              <div class="flex flex-wrap gap-2">
                <button v-for="opt in [{v:'',l:'All Time'},{v:'today',l:'Today'},{v:'yesterday',l:'Yesterday'},{v:'this_week',l:'This Week'},{v:'last_week',l:'Last Week'},{v:'this_month',l:'This Month'},{v:'last_month',l:'Last Month'},{v:'this_year',l:'This Year'},{v:'last_year',l:'Last Year'}]" :key="opt.v"
                  @click="f.date_preset = opt.v; onDatePresetChange()"
                  class="text-xs font-semibold px-3.5 py-2 rounded-full transition"
                  :class="f.date_preset === opt.v ? 'bg-indigo-500 text-white shadow-sm' : 'bg-slate-50 dark:bg-slate-800 text-slate-500 dark:text-slate-400 border border-slate-200 dark:border-slate-700'">
                  {{ opt.l }}
                </button>
              </div>
              <!-- Custom date inputs -->
              <div v-if="f.date_preset === 'custom'" class="flex items-center gap-2 mt-3">
                <input v-model="f.date_from" type="date" @change="applyFilters"
                  class="flex-1 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700
                         text-slate-700 dark:text-slate-300 rounded-xl px-3 py-2 text-sm
                         focus:outline-none focus:ring-2 focus:ring-indigo-500 transition" />
                <span class="text-slate-400 text-xs shrink-0">to</span>
                <input v-model="f.date_to" type="date" @change="applyFilters"
                  class="flex-1 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700
                         text-slate-700 dark:text-slate-300 rounded-xl px-3 py-2 text-sm
                         focus:outline-none focus:ring-2 focus:ring-indigo-500 transition" />
              </div>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>

    <!-- Mobile customer bottom sheet -->
    <Teleport to="body">
      <!-- Backdrop -->
      <Transition
        enter-active-class="transition-opacity duration-300"
        enter-from-class="opacity-0" enter-to-class="opacity-100"
        leave-active-class="transition-opacity duration-200"
        leave-from-class="opacity-100" leave-to-class="opacity-0">
        <div v-if="mobileCustomerSheetOpen"
          class="md:hidden fixed inset-0 z-40 bg-black/40 backdrop-blur-sm"
          @click="mobileCustomerSheetOpen = false">
        </div>
      </Transition>

      <!-- Sheet -->
      <Transition
        enter-active-class="transition-transform duration-300 ease-[cubic-bezier(0.32,0.72,0,1)]"
        enter-from-class="translate-y-full" enter-to-class="translate-y-0"
        leave-active-class="transition-transform duration-200 ease-in"
        leave-from-class="translate-y-0" leave-to-class="translate-y-full">
        <div v-if="mobileCustomerSheetOpen"
          class="md:hidden fixed inset-x-0 z-50 flex flex-col bg-white dark:bg-slate-900 rounded-t-3xl shadow-2xl"
          :style="{ bottom: sheetBottom + 'px', maxHeight: sheetMaxHeight }">

          <!-- Handle -->
          <div class="pt-3 pb-1 flex justify-center shrink-0">
            <div class="w-10 h-1 rounded-full bg-slate-200 dark:bg-slate-700"></div>
          </div>

          <!-- Header -->
          <div class="flex items-center justify-between px-5 py-3 shrink-0">
            <h3 class="text-base font-bold text-slate-800 dark:text-white">Select Customer</h3>
            <button @click="mobileCustomerSheetOpen = false"
              class="w-8 h-8 flex items-center justify-center rounded-full bg-slate-100 dark:bg-slate-800 text-slate-500 active:scale-90 transition">
              <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>

          <!-- Search -->
          <div class="px-4 pb-3 shrink-0">
            <div class="relative">
              <svg class="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400"
                fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z"/>
              </svg>
              <input v-model="customerSearch" type="text" placeholder="Search by name or shop…"
                autocomplete="off" autocorrect="off" autocapitalize="off" spellcheck="false"
                class="w-full bg-slate-100 dark:bg-slate-800 text-slate-800 dark:text-white placeholder-slate-400
                       rounded-2xl pl-10 pr-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 transition" />
            </div>
          </div>

          <!-- List -->
          <div class="overflow-y-auto flex-1 px-3 pb-8">

            <!-- All Customers -->
            <button type="button" @click="selectMobileCustomer('')"
              class="w-full flex items-center gap-3 px-3 py-3 rounded-2xl mb-1 transition active:scale-[0.98]"
              :class="!f.customer_id ? 'bg-indigo-50 dark:bg-indigo-950' : 'hover:bg-slate-50 dark:hover:bg-slate-800/60'">
              <span class="w-10 h-10 rounded-xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center shrink-0">
                <svg class="w-5 h-5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.8">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z"/>
                </svg>
              </span>
              <span class="text-sm font-semibold flex-1 text-left"
                :class="!f.customer_id ? 'text-indigo-600 dark:text-indigo-400' : 'text-slate-700 dark:text-slate-300'">
                All Customers
              </span>
              <svg v-if="!f.customer_id" class="w-4 h-4 text-indigo-500 shrink-0"
                fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                <path stroke-linecap="round" stroke-linejoin="round" d="M4.5 12.75l6 6 9-13.5"/>
              </svg>
            </button>

            <!-- Customer rows -->
            <button v-for="c in filteredCustomers" :key="c.id" type="button"
              @click="selectMobileCustomer(String(c.id))"
              class="w-full flex items-center gap-3 px-3 py-3 rounded-2xl mb-1 transition active:scale-[0.98]"
              :class="String(c.id) === f.customer_id ? 'bg-indigo-50 dark:bg-indigo-950' : 'hover:bg-slate-50 dark:hover:bg-slate-800/60'">
              <span class="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-100 to-violet-100 dark:from-indigo-900/60 dark:to-violet-900/60 flex items-center justify-center shrink-0 text-sm font-bold text-indigo-600 dark:text-indigo-400">
                {{ c.name.charAt(0).toUpperCase() }}
              </span>
              <div class="min-w-0 flex-1 text-left">
                <p class="text-sm font-semibold truncate"
                  :class="String(c.id) === f.customer_id ? 'text-indigo-600 dark:text-indigo-400' : 'text-slate-800 dark:text-white'">
                  {{ c.name }}
                </p>
                <p class="text-xs text-slate-400 truncate mt-0.5">{{ c.shop_name }}</p>
              </div>
              <svg v-if="String(c.id) === f.customer_id" class="w-4 h-4 text-indigo-500 shrink-0"
                fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                <path stroke-linecap="round" stroke-linejoin="round" d="M4.5 12.75l6 6 9-13.5"/>
              </svg>
            </button>

            <p v-if="filteredCustomers.length === 0"
              class="text-center text-slate-400 text-sm py-10">No customers found</p>
          </div>
        </div>
      </Transition>
    </Teleport>

    <!-- Mobile Locked Alert -->
    <Teleport to="body">
      <Transition
        enter-active-class="transition ease-out duration-200"
        enter-from-class="opacity-0 translate-y-4"
        enter-to-class="opacity-100 translate-y-0"
        leave-active-class="transition ease-in duration-150"
        leave-from-class="opacity-100 translate-y-0"
        leave-to-class="opacity-0 translate-y-4">
        <div v-if="showLockedAlert" class="md:hidden fixed inset-x-0 bottom-0 z-50 p-4">
          <div class="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl shadow-xl overflow-hidden">
            <div class="flex items-start gap-3 p-4">
              <div class="w-10 h-10 rounded-xl bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center shrink-0">
                <svg class="w-5 h-5 text-amber-600 dark:text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z"/>
                </svg>
              </div>
              <div class="flex-1 min-w-0">
                <p class="text-sm font-semibold text-slate-800 dark:text-white mb-1">Cannot Edit Invoice</p>
                <p class="text-sm text-slate-600 dark:text-slate-300">{{ lockedAlertMessage }}</p>
              </div>
              <button @click="closeLockedAlert" class="shrink-0 w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition">
                <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </button>
            </div>
            <button @click="closeLockedAlert" class="w-full bg-slate-50 dark:bg-slate-800 text-slate-700 dark:text-slate-300 text-sm font-semibold py-3 border-t border-slate-200 dark:border-slate-700 active:bg-slate-100 dark:active:bg-slate-700 transition">
              Got it
            </button>
          </div>
        </div>
      </Transition>
    </Teleport>

    <!-- Delete confirmation -->
    <ConfirmDialog
      v-if="showDeleteConfirm"
      title="Permanently Delete Invoice?"
      message="This action cannot be undone. The invoice will be permanently removed from your account."
      info-message="WARNING: This will permanently delete this invoice and remove it from all reports and statistics. This action is irreversible."
      confirm-text="Delete Permanently"
      loading-text="Deleting..."
      variant="danger"
      @confirm="confirmDelete"
      @cancel="closeDeleteConfirm" />

  </AppLayout>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue'
import { Link, router, usePage } from '@inertiajs/vue3'
import AppLayout from '@/layouts/AppLayout.vue'
import ConfirmDialog from '@/components/ConfirmDialog.vue'

const props = defineProps({ customers: Array, filters: Object })
const page = usePage()
const businessName = computed(() => page.props.auth?.user?.business_name?.trim() || 'Master Ledger')

const invoices     = ref([])
const meta         = ref({ total: 0, pages: 1, current_page: 1, per_page: 10 })
const dataLoading  = ref(true)
const loadingMore  = ref(false)

const mobileStatSales     = computed(() => invoices.value.filter(i => i.action === 'sold').reduce((s, i) => s + parseFloat(i.total || 0), 0))
const mobileStatPurchases = computed(() => invoices.value.filter(i => i.action === 'purchased').reduce((s, i) => s + parseFloat(i.total || 0), 0))

async function fetchData(append = false) {
  if (append) loadingMore.value = true
  else dataLoading.value = true
  const p = new URLSearchParams()
  if (f.value.customer_id)    p.set('customer_id',    f.value.customer_id)
  if (f.value.invoice_action) p.set('invoice_action', f.value.invoice_action)
  if (f.value.date_preset)    p.set('date_preset',    f.value.date_preset)
  if (f.value.date_preset === 'custom') {
    if (f.value.date_from) p.set('date_from', f.value.date_from)
    if (f.value.date_to)   p.set('date_to',   f.value.date_to)
  }
  if (f.value.per_page !== 10) p.set('per_page', f.value.per_page)
  if (f.value.page > 1)        p.set('page',     f.value.page)

  const qs = p.toString()
  const url = qs ? `/invoices?${qs}` : '/invoices'
  history.replaceState(null, '', url)

  try {
    const res  = await fetch(`/invoices/data${qs ? '?' + qs : ''}`, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
    const json = await res.json()
    const incoming = json.invoices || []
    invoices.value = append ? [...invoices.value, ...incoming] : incoming
    meta.value     = json.meta || { total: 0, pages: 1, current_page: 1, per_page: 10 }
  } finally {
    dataLoading.value = false
    loadingMore.value = false
  }
}

function loadMore() {
  f.value.page++
  fetchData(true)
}

const perPageOptions = [10, 20, 50, 100, 250, 500, 1000]

// ── Filter state ────────────────────────────────────
const f = ref({
  customer_id:    props.filters?.customer_id    || '',
  invoice_action: props.filters?.invoice_action || '',
  date_preset:    props.filters?.date_preset    || '',
  date_from:      props.filters?.date_from      || '',
  date_to:        props.filters?.date_to        || '',
  per_page:       props.filters?.per_page       || 10,
  page:           props.filters?.page           || 1,
})

const hasFilters = computed(() => f.value.customer_id || f.value.invoice_action || f.value.date_preset)

function applyFilters() {
  f.value.page = 1
  fetchData()
}

function onDatePresetChange() {
  if (f.value.date_preset !== 'custom') { f.value.date_from = ''; f.value.date_to = '' }
  applyFilters()
}

function clearFilters() {
  f.value = { customer_id: '', invoice_action: '', date_preset: '', date_from: '', date_to: '', per_page: f.value.per_page, page: 1 }
  fetchData()
}

// ── Pagination ──────────────────────────────────────
function goToPage(pg) {
  if (pg < 1 || pg > meta.value.pages) return
  f.value.page = pg
  fetchData()
}

const pageNumbers = computed(() => {
  const total = meta.value.pages, cur = meta.value.current_page
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1)
  if (cur <= 4)          return [1, 2, 3, 4, 5, '...', total]
  if (cur >= total - 3)  return [1, '...', total-4, total-3, total-2, total-1, total]
  return [1, '...', cur-1, cur, cur+1, '...', total]
})

// ── Customer filter dropdown ────────────────────────
const customerDropdownOpen    = ref(false)
const customerSearch          = ref('')
const filterCustomerRef       = ref(null)
const customerItemRefs        = ref([])
const activeCustomerIdx       = ref(-1)
const mobileFiltersSheetOpen   = ref(false)
const mobileFiltersActiveCount = computed(() =>
  (f.value.customer_id ? 1 : 0) + (f.value.date_preset ? 1 : 0)
)

const mobileCustomerSheetOpen = ref(false)
const sheetBottom             = ref(0)
const sheetMaxHeight          = ref('82vh')

function onViewportResize() {
  const vv = window.visualViewport
  if (!vv) return
  const kbHeight = Math.max(0, window.innerHeight - vv.offsetTop - vv.height)
  sheetBottom.value   = kbHeight
  sheetMaxHeight.value = kbHeight > 0 ? (vv.height * 0.92) + 'px' : '82vh'
}

watch(mobileCustomerSheetOpen, (open) => {
  if (open) {
    sheetBottom.value    = 0
    sheetMaxHeight.value = '82vh'
    window.visualViewport?.addEventListener('resize', onViewportResize)
  } else {
    window.visualViewport?.removeEventListener('resize', onViewportResize)
    sheetBottom.value    = 0
    sheetMaxHeight.value = '82vh'
  }
})

const filteredCustomers = computed(() => {
  const q = customerSearch.value.trim().toLowerCase()
  if (!q) return props.customers
  return props.customers.filter(c => c.name.toLowerCase().includes(q) || c.shop_name.toLowerCase().includes(q))
})

const filterCustomerLabel = computed(() => {
  if (!f.value.customer_id) return 'All Customers'
  return props.customers.find(c => String(c.id) === f.value.customer_id)?.name || 'All Customers'
})

function toggleCustomerDropdown() {
  customerDropdownOpen.value = !customerDropdownOpen.value
  if (customerDropdownOpen.value) { customerSearch.value = ''; activeCustomerIdx.value = -1; customerItemRefs.value = [] }
}

function selectCustomer(id) {
  f.value.customer_id        = id
  f.value.page               = 1
  customerDropdownOpen.value = false
  applyFilters()
}

function moveCustomer(dir) {
  const len = filteredCustomers.value.length
  if (!len) return
  activeCustomerIdx.value = (activeCustomerIdx.value + dir + len) % len
  customerItemRefs.value[activeCustomerIdx.value]?.scrollIntoView({ block: 'nearest' })
}

function confirmCustomer() {
  if (activeCustomerIdx.value >= 0 && filteredCustomers.value[activeCustomerIdx.value]) {
    selectCustomer(String(filteredCustomers.value[activeCustomerIdx.value].id))
  }
}

watch(customerSearch, () => { activeCustomerIdx.value = -1; customerItemRefs.value = [] })

function onClickOutside(e) {
  if (!filterCustomerRef.value?.contains(e.target)) customerDropdownOpen.value = false
}

function selectMobileCustomer(id) {
  f.value.customer_id        = id
  f.value.page               = 1
  mobileCustomerSheetOpen.value = false
  customerSearch.value       = ''
  applyFilters()
}

onMounted(() => {
  document.title = `Invoices | ${businessName.value}`
  document.addEventListener('click', onClickOutside)
  document.addEventListener('click', closeActionDropdown)
  fetchData()
})
onBeforeUnmount(() => {
  document.removeEventListener('click', onClickOutside)
  document.removeEventListener('click', closeActionDropdown)
})

// ── Row expand ──────────────────────────────────────
const expandedId = ref(null)
function toggleExpand(id) {
  expandedId.value = expandedId.value === id ? null : id
}

// ── Action dropdown ────────────────────────────────
const actionDropdownId = ref(null)
function toggleActionDropdown(id) {
  actionDropdownId.value = actionDropdownId.value === id ? null : id
}
function closeActionDropdown() {
  actionDropdownId.value = null
}

// ── Share link ───────────────────────────────────────
const copiedId = ref(null)
function copyInvoiceLink(id) {
  const url = `${window.location.origin}/invoices/${id}/view`
  navigator.clipboard.writeText(url)
  copiedId.value = id
  setTimeout(() => { copiedId.value = null }, 2000)
}

// ── Toggle public ─────────────────────────────────────
function togglePublic(inv) {
  inv.public = !inv.public
  router.patch(`/invoices/${inv.id}/toggle_public`, {}, { preserveState: true, preserveScroll: true })
}

// ── Handle locked edit click (mobile alert) ──────────
const showLockedAlert = ref(false)
const lockedAlertMessage = ref('')

function handleLockedEditClick(inv) {
  // Only show alert on mobile
  if (window.innerWidth < 768) {
    if (inv.customer_deleted) {
      lockedAlertMessage.value = 'Cannot edit this invoice because the customer has been deleted.'
    } else if (inv.has_deleted_products) {
      lockedAlertMessage.value = `Cannot edit this invoice because the following products have been deleted: ${inv.deleted_products_names.join(', ')}`
    }
    showLockedAlert.value = true
  }
}

function closeLockedAlert() {
  showLockedAlert.value = false
  lockedAlertMessage.value = ''
}

// ── Delete invoice ─────────────────────────────────
const showDeleteConfirm = ref(false)
const deleteTarget = ref(null)

function openDeleteConfirm(invoice) {
  deleteTarget.value = invoice
  showDeleteConfirm.value = true
}

function closeDeleteConfirm() {
  showDeleteConfirm.value = false
  deleteTarget.value = null
}

function confirmDelete() {
  return new Promise((resolve) => {
    router.delete(`/invoices/${deleteTarget.value.id}`, {
      onSuccess: () => {
        closeDeleteConfirm()
        fetchData()
        resolve()
      },
      onError: () => {
        resolve()
      }
    })
  })
}

// ── Helpers ─────────────────────────────────────────
function formatNum(n) { return Math.floor(Number(n)).toLocaleString('en-PK') }

function formatDate(d) {
  if (!d) return '—'
  const [y, m, day] = d.split('-')
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
  return `${parseInt(day)} ${months[parseInt(m) - 1]} ${y}`
}
</script>
